import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ObjectMarker } from '@sas/nova-commons/components/ObjectMarker';
import { Icon } from '@sas/nova-commons/components/Icon';
import { assertNever } from '@sas/nova-core/typeguards';
import { CopyFromDataSource, ErrorStatus, Image, InformationStatus, JobTemplate, Stop, CanceledStatus, Play, Sync, } from '@sas/react-svg';
import { Flow } from '@sas/nova-commons/components/Layouts/Flow';
import { renderInlineCssVars, useDesignTokens } from '@sas/nova-commons/theme';
import { useI18n, useLoadingText } from '../../locale/I18nContext';
import cx from 'classnames';
import cn from './ObjectStatus.css.json';
import './ObjectStatus.css';
// Current UI spec https://gitlab.sas.com/dvr/html/resource-archive/blob/master/specs/object-status.pdf
export const ObjectStatus = function (props) {
    var _a;
    const loadingText = useLoadingText();
    const localize = useI18n();
    const dt = useDesignTokens();
    const tokens = ObjectStatusTokens(dt, props);
    const objectStatusTokens = Object.assign(Object.assign({ 
        // TODO: maybe we should put these in the react rendered design tokens as the zero value?
        '--objectstatus-bd': 'transparent' }, renderInlineCssVars(Object.assign(Object.assign({}, tokens), { padding: dt.tokens['space-base-s'] }), 'objectstatus')), renderInlineCssVars({
        outline: 'var(--focus-light-bg)',
        'outline-offset': 'calc(-2px + calc(-1 * var(--focus-width)))',
    }, 'objectstatus-focus'));
    let clickableStyle = {};
    let ariaRole;
    if (props.onPress) {
        clickableStyle = { cursor: 'pointer' };
        ariaRole = 'button';
    }
    let icon;
    let defaultText;
    switch (props.mode) {
        case 'information':
            icon = _jsx(Icon, { icon: InformationStatus, className: cn.icon, color: "informational", tooltip: "" });
            break;
        case 'error':
            icon = _jsx(Icon, { icon: ErrorStatus, className: cn.icon, color: "alert", tooltip: "" });
            break;
        case 'busy':
            // Disable tab focus on the ObjectMarker element. We let the entire ObjectStatus pill handle focus.
            icon = _jsx(ObjectMarker, { type: "AnimatedInProgress", text: "", focusable: false, iconTooltip: "" });
            defaultText = loadingText;
            break;
        case 'cancelLoading':
            icon = _jsx(Icon, { icon: Stop, className: cn.icon, tooltip: "" });
            defaultText = localize({ key: 'ObjectStatus.cancelLoading.txt' });
            break;
        case 'loadingCanceled':
            icon = _jsx(Icon, { icon: CanceledStatus, className: cn.icon, tooltip: "" });
            defaultText = localize({ key: 'ObjectStatus.loadingCanceled.txt' });
            break;
        case 'resumeLoading':
            icon = _jsx(Icon, { icon: Play, className: cn.icon, tooltip: "" });
            defaultText = localize({ key: 'ObjectStatus.resumeLoading.txt' });
            break;
        case 'assignData':
            icon = _jsx(Icon, { icon: CopyFromDataSource, className: cn.icon, tooltip: "" });
            defaultText = localize({ key: 'ObjectStatus.assignData.txt' });
            break;
        case 'selectImage':
            icon = _jsx(Icon, { icon: Image, className: cn.icon, tooltip: "" });
            defaultText = localize({ key: 'ObjectStatus.selectImage.txt' });
            break;
        case 'selectJob':
            icon = _jsx(Icon, { icon: JobTemplate, className: cn.icon, tooltip: "" });
            defaultText = localize({ key: 'ObjectStatus.selectJob.txt' });
            break;
        case 'setJobParameters':
            icon = _jsx(Icon, { icon: JobTemplate, className: cn.icon, tooltip: "" });
            defaultText = localize({ key: 'ObjectStatus.setJobParameters.txt' });
            break;
        case 'refresh':
            icon = _jsx(Icon, { icon: Sync, className: cn.icon, tooltip: "" });
            defaultText = localize({ key: 'ObjectStatus.refresh.txt' });
            break;
        default:
            assertNever(props.mode);
    }
    const statusText = (_a = props.text) !== null && _a !== void 0 ? _a : defaultText;
    return (_jsxs(Flow, Object.assign({ role: ariaRole, "aria-label": props['aria-label'], title: statusText, className: cx(!statusText && props.mode === 'busy' ? cn.busySingle : cn.base), style: Object.assign(Object.assign(Object.assign({}, objectStatusTokens), clickableStyle), props.style), spacing: "s", onClick: props.onPress, onKeyPress: (e) => {
            var _a;
            if (e.key === 'Enter' || e.key === ' ') {
                (_a = props.onPress) === null || _a === void 0 ? void 0 : _a.call(props);
            }
        }, onMouseEnter: props.onMouseEnter, onMouseLeave: props.onMouseLeave, tabIndex: 0 }, { children: [icon, statusText ? _jsx("span", Object.assign({ className: cn.text }, { children: statusText })) : null] })));
};
function ObjectStatusTokens(dt, props) {
    switch (props.mode) {
        case 'error':
            return dt.themeVariant === 'high-contrast'
                ? dt.reportTokens.stateStatus.alert
                : dt.reportTokens.stateStatusPadBox.alert;
        case 'information':
            return dt.themeVariant === 'high-contrast'
                ? dt.reportTokens.stateStatus.informational
                : dt.reportTokens.stateStatusPadBox.informational;
        default:
            switch (dt.themeVariant) {
                case 'dark':
                    return { bg: dt.c.p_3, bd: dt.c.white30, fg: dt.reportTokens.text.description.fg };
                case 'high-contrast':
                    return { bg: dt.c.black, bd: dt.c.white, fg: dt.reportTokens.text.description.fg };
                case 'light':
                    return { bg: dt.c.white, bd: dt.c.n2, fg: dt.reportTokens.text.description.fg };
                default:
                    return assertNever(dt.themeVariant);
            }
    }
}
